import { Link } from 'react-router-dom';

import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/Badge';
import { Icon, type IconName } from '@/shared/ui/Icon';
import { PageHeader } from '@/shared/ui/PageHeader';

interface Cartao {
  titulo: string;
  descricao: string;
  icone: IconName;
  /** Destino do cartao. Sem destino, a tela ainda nao existe. */
  para?: string;
  /** Onde a informacao fica hoje, para o cartao que ainda nao tem tela. */
  hoje?: { rotulo: string; para: string; permissao: string }[];
}

/**
 * Os cartoes da parametrizacao do OnPoint (`/parametrizacao/menu`).
 *
 * No OnPoint o menu tem Perfil da Corretora, Modalidades e Consultas PJ e
 * Pre-validacao; endosso e renovacao existem so por endereco. Aqui os dois aparecem
 * como cartao (decisao 8 da secao 11.6 do plano 49). Perfil da Corretora e Consultas
 * PJ e Pre-validacao tem tela desde o P-PAR-4; Endosso e Renovacao, desde o P-PAR-3.
 * O cartao sem tela diz onde a informacao fica hoje no SeguraCore.
 */
const cartoesDoOnPoint: Cartao[] = [
  {
    titulo: 'Perfil da Corretora',
    descricao: 'Perfis de acesso da corretora e o que cada um pode fazer.',
    icone: 'users',
    para: '/parametrizacao/perfil-corretora',
  },
  {
    titulo: 'Modalidades',
    descricao:
      'Ramos, grupos e modalidades, com a configuracao versionada de cada modalidade.',
    icone: 'layers',
    para: '/parametrizacao/modalidades',
  },
  {
    titulo: 'Consultas PJ e Pre-validacao',
    descricao:
      'Dados de acesso as consultas e as regras da consulta PJ e da pre-validacao.',
    icone: 'target',
    para: '/parametrizacao/consulta-pj',
  },
  {
    titulo: 'Endosso',
    descricao:
      'Endosso de aniversario: aviso por e-mail, objeto e modalidades com indice.',
    icone: 'file',
    para: '/parametrizacao/endosso',
  },
  {
    titulo: 'Renovacao',
    descricao:
      'Renovacao automatica de apolices: aviso, perfis avisados e prazos por modalidade.',
    icone: 'refresh',
    para: '/parametrizacao/renovacao',
  },
];

/** O que o SeguraCore ja parametrizava fora do menu do OnPoint, e continua valendo. */
const cartoesDoSeguraCore: (Cartao & { permissao: string })[] = [
  {
    titulo: 'Tabela de taxas',
    descricao: 'Faixas de taxa por modalidade e valor garantido.',
    icone: 'percent',
    para: '/taxas',
    permissao: 'taxas.tabela.view',
  },
  {
    titulo: 'Agravos e descontos',
    descricao: 'Fatores que ajustam a taxa a partir da taxa base.',
    icone: 'coins',
    para: '/agravos',
    permissao: 'agravos.fator.view',
  },
];

const classeDoCartao =
  'flex h-full flex-col gap-3 rounded-lg border border-line bg-surface p-5 text-left';

function ConteudoDoCartao({ cartao }: { cartao: Cartao }) {
  return (
    <>
      <div className="flex items-center gap-3">
        <span className="flex size-9 items-center justify-center rounded-md bg-brand-50 text-brand-700">
          <Icon name={cartao.icone} size={18} />
        </span>
        <h3 className="text-sm font-semibold text-ink-900">{cartao.titulo}</h3>
      </div>
      <p className="text-xs text-ink-600">{cartao.descricao}</p>
    </>
  );
}

/** Menu da parametrizacao (`/parametrizacao`). */
export function ParametrizationMenuPage() {
  const { can } = useAuth();
  const doSeguraCore = cartoesDoSeguraCore.filter((cartao) => can(cartao.permissao));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Parametrizacao"
        description="Parametros da empresa: modalidades, perfis da corretora, consultas, endosso e renovacao."
      />

      <ul
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        aria-label="Cartoes da parametrizacao"
      >
        {cartoesDoOnPoint.map((cartao) => (
          <li key={cartao.titulo}>
            {cartao.para ? (
              <Link
                to={cartao.para}
                className={cn(
                  classeDoCartao,
                  'transition-colors hover:border-brand-300 hover:bg-brand-50/40',
                )}
              >
                <ConteudoDoCartao cartao={cartao} />
              </Link>
            ) : (
              <div className={classeDoCartao} aria-label={cartao.titulo}>
                <ConteudoDoCartao cartao={cartao} />
                <Badge tone="info">Tela em preparacao</Badge>
                <p className="text-2xs text-ink-500">
                  A tela desta parametrizacao vem em uma proxima entrega.
                  {(cartao.hoje ?? []).some((item) => can(item.permissao))
                    ? ' Ate la, o que existe fica em:'
                    : ''}
                </p>
                {(cartao.hoje ?? []).some((item) => can(item.permissao)) ? (
                  <ul className="flex flex-wrap gap-2">
                    {(cartao.hoje ?? [])
                      .filter((item) => can(item.permissao))
                      .map((item) => (
                        <li key={item.para}>
                          <Link
                            to={item.para}
                            className="text-xs font-medium text-brand-700 hover:underline"
                          >
                            {item.rotulo}
                          </Link>
                        </li>
                      ))}
                  </ul>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>

      {doSeguraCore.length > 0 ? (
        <section className="flex flex-col gap-3" aria-label="Tambem na parametrizacao">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Tambem na parametrizacao
          </h3>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {doSeguraCore.map((cartao) => (
              <li key={cartao.titulo}>
                <Link
                  to={cartao.para ?? '/parametrizacao'}
                  className={cn(
                    classeDoCartao,
                    'transition-colors hover:border-brand-300 hover:bg-brand-50/40',
                  )}
                >
                  <ConteudoDoCartao cartao={cartao} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
